import React, { useState, useMemo } from "react";
import { Icon } from "./Icons";
import { buildIntakeForTherapist, paginateQuestions } from "./intakeQuestions";
import "./IntakeForm.css";

// Generic fallback set when no therapist (free consultation flow)
const GENERIC_QUESTIONS = [
    {
        id: "concerns_summary",
        type: "textarea",
        label: "What's bringing you in?",
        placeholder: "Brief is fine. We'll get into it on the call.",
        required: true,
    },
    {
        id: "therapy_goals",
        type: "textarea",
        label: "What would you like to be different?",
        placeholder: "Even a vague answer is fine.",
        required: true,
    },
    {
        id: "previous_therapy",
        type: "radio",
        label: "Have you been in therapy before?",
        options: [
            { value: "no", label: "No, this is my first time" },
            { value: "yes-helpful", label: "Yes, and it was helpful" },
            { value: "yes-mixed", label: "Yes, with mixed results" },
            { value: "yes-not-helpful", label: "Yes, but it wasn't a fit" },
        ],
        required: true,
    },
];

function QuestionField({ question, value, onChange }) {
    const set = (v) => onChange(question.id, v);

    switch (question.type) {
        case "text":
            return (
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => set(e.target.value)}
                    placeholder={question.placeholder}
                    required={question.required}
                />
            );
        case "textarea":
            return (
                <textarea
                    rows="4"
                    value={value || ""}
                    onChange={(e) => set(e.target.value)}
                    placeholder={question.placeholder}
                    required={question.required}
                />
            );
        case "select":
            return (
                <select
                    value={value || ""}
                    onChange={(e) => set(e.target.value)}
                    required={question.required}
                >
                    <option value="">Select an option</option>
                    {question.options.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            );
        case "radio":
            return (
                <div className="radio-group radio-group-vertical">
                    {question.options.map((opt) => (
                        <label
                            key={opt.value}
                            className={`radio-card ${value === opt.value ? "selected" : ""}`}
                        >
                            <input
                                type="radio"
                                name={question.id}
                                value={opt.value}
                                checked={value === opt.value}
                                onChange={() => set(opt.value)}
                            />
                            <span className="radio-label">{opt.label}</span>
                        </label>
                    ))}
                </div>
            );
        case "checkbox": {
            const arr = Array.isArray(value) ? value : [];
            return (
                <div className="checkbox-grid">
                    {question.options.map((opt) => {
                        const checked = arr.includes(opt);
                        return (
                            <button
                                key={opt}
                                type="button"
                                className={`concern-option ${checked ? "selected" : ""}`}
                                onClick={() =>
                                    set(checked ? arr.filter((v) => v !== opt) : [...arr, opt])
                                }
                            >
                                {checked && <Icon name="check" size={14} className="concern-check" />}
                                <span>{opt}</span>
                            </button>
                        );
                    })}
                </div>
            );
        }
        case "scale": {
            const numeric = Number(value) || question.min;
            return (
                <div className="scale-input">
                    <div className="scale-labels">
                        <span>{question.minLabel || question.min}</span>
                        <span className="scale-current">{numeric}</span>
                        <span>{question.maxLabel || question.max}</span>
                    </div>
                    <input
                        type="range"
                        min={question.min}
                        max={question.max}
                        value={numeric}
                        onChange={(e) => set(Number(e.target.value))}
                        className="scale-slider"
                    />
                </div>
            );
        }
        default:
            return null;
    }
}

function IntakeForm({ bookingData, therapist, onComplete }) {
    const questions = useMemo(
        () => (therapist ? buildIntakeForTherapist(therapist) : GENERIC_QUESTIONS),
        [therapist]
    );

    const pages = useMemo(() => paginateQuestions(questions, 5), [questions]);

    const [currentPage, setCurrentPage] = useState(0);
    const [answers, setAnswers] = useState({});

    const handleChange = (id, value) => {
        setAnswers((prev) => ({ ...prev, [id]: value }));
    };

    const visibleQuestions = pages[currentPage].filter(
        (q) => !q.showIf || q.showIf(answers)
    );

    const canProceed = visibleQuestions.every((q) => {
        if (!q.required) return true;
        const v = answers[q.id];
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && v !== null && v !== "";
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (currentPage < pages.length - 1) {
            setCurrentPage((p) => p + 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            onComplete(answers);
        }
    };

    const goBack = () => {
        if (currentPage > 0) {
            setCurrentPage((p) => p - 1);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const totalPages = pages.length;
    const progress = ((currentPage + 1) / totalPages) * 100;

    return (
        <div className="intake-container">
            <div className="intake-header">
                <h2>A few quick questions</h2>
                <p>
                    {questions.length} questions across {totalPages} short pages.
                    {therapist && (
                        <>
                            {" "}
                            Tailored for {therapist.name}'s practice.
                        </>
                    )}
                </p>
            </div>

            <div className="intake-disclaimer">
                <Icon name="lock" size={18} />
                <div>
                    <strong>This is not your clinical intake.</strong>
                    <p>
                        Full health history, medications, and emergency contact info are
                        collected separately through the HIPAA-compliant client portal
                        after booking is confirmed. This form helps your therapist get
                        oriented before your first session.
                    </p>
                </div>
            </div>

            <div className="intake-booking-info">
                <div className="info-item">
                    <span className="info-label">Scheduled appointment</span>
                    <span className="info-value">
                        {bookingData.service.name} ·{" "}
                        {bookingData.date.toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                        })}{" "}
                        at {bookingData.time}
                    </span>
                </div>
                {therapist && (
                    <div className="info-item">
                        <span className="info-label">Therapist</span>
                        <span className="info-value">
                            {therapist.name}, {therapist.credential}
                        </span>
                    </div>
                )}
                <div className="info-item">
                    <span className="info-label">Client</span>
                    <span className="info-value">
                        {bookingData.client.firstName} {bookingData.client.lastName}
                    </span>
                </div>
            </div>

            <div className="intake-progress">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <span className="progress-text">
                    Page {currentPage + 1} of {totalPages}
                </span>
            </div>

            <form onSubmit={handleSubmit} className="intake-form">
                <div className="intake-section animate-in">
                    {visibleQuestions.map((q) => (
                        <div key={q.id} className="form-group intake-question">
                            <label className="intake-question-label">
                                {q.label}
                                {q.required && <span className="required-marker"> *</span>}
                            </label>
                            <QuestionField
                                question={q}
                                value={answers[q.id]}
                                onChange={handleChange}
                            />
                        </div>
                    ))}
                </div>

                <div className="intake-actions">
                    {currentPage > 0 ? (
                        <button type="button" className="btn-secondary" onClick={goBack}>
                            Previous
                        </button>
                    ) : (
                        <span />
                    )}

                    <button type="submit" className="btn-primary" disabled={!canProceed}>
                        {currentPage < totalPages - 1 ? "Continue" : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default IntakeForm;